import { FREE_TIER_LIMITS, PRO_TIER_LIMITS, type UserTier } from "@shared/schema";

export type UsageType = 'optimization' | 'mapView';

interface UsageData {
    daily: number;
    monthly: number;
    lastDailyReset: string; // ISO date string (YYYY-MM-DD)
    lastMonthlyReset: string; // ISO date string (YYYY-MM)
}

export function getTierLimits(tier: UserTier) {
    if (tier === 'pro' || tier === 'power') return PRO_TIER_LIMITS;
    return FREE_TIER_LIMITS;
}

export function checkUsageLimit(
    type: UsageType,
    tier: UserTier
): { canProceed: boolean; limit: number; period: 'daily' | 'monthly' } {
    const limits = getTierLimits(tier);
    const storageKey = `vintmap_usage_${type}`;
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const thisMonth = today.substring(0, 7);

    let usage: UsageData = {
        daily: 0,
        monthly: 0,
        lastDailyReset: today,
        lastMonthlyReset: thisMonth
    };

    const stored = localStorage.getItem(storageKey);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            usage = { ...usage, ...parsed };
        } catch (e) {
            console.error("Failed to parse usage data", e);
        }
    }

    // Handle resets
    if (usage.lastDailyReset !== today) {
        usage.daily = 0;
        usage.lastDailyReset = today;
    }
    if (usage.lastMonthlyReset !== thisMonth) {
        usage.monthly = 0;
        usage.lastMonthlyReset = thisMonth;
    }

    const dailyLimit = type === 'optimization'
        ? (limits as any).optimizationsPerDay
        : (limits as any).interactiveMapViewsPerDay;

    const monthlyLimit = type === 'optimization'
        ? (limits as any).optimizationsPerMonth
        : (limits as any).interactiveMapViewsPerMonth;

    if (usage.daily >= dailyLimit) {
        return { canProceed: false, limit: dailyLimit, period: 'daily' };
    }
    if (usage.monthly >= monthlyLimit) {
        return { canProceed: false, limit: monthlyLimit, period: 'monthly' };
    }

    return { canProceed: true, limit: 0, period: 'daily' };
}

export function incrementUsage(type: UsageType) {
    const storageKey = `vintmap_usage_${type}`;
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const thisMonth = today.substring(0, 7);

    let usage: UsageData = {
        daily: 0,
        monthly: 0,
        lastDailyReset: today,
        lastMonthlyReset: thisMonth
    };

    const stored = localStorage.getItem(storageKey);
    if (stored) {
        try {
            usage = { ...usage, ...JSON.parse(stored) };
        } catch (e) { }
    }

    // Handle resets before incrementing
    if (usage.lastDailyReset !== today) {
        usage.daily = 0;
        usage.lastDailyReset = today;
    }
    if (usage.lastMonthlyReset !== thisMonth) {
        usage.monthly = 0;
        usage.lastMonthlyReset = thisMonth;
    }

    usage.daily += 1;
    usage.monthly += 1;

    localStorage.setItem(storageKey, JSON.stringify(usage));
}
